import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Bookmark, Plus, Pencil, Trash2, RefreshCw, ShieldCheck, Users2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Standalone Permission Templates Manager
 *
 * Full CRUD over `db.grant_templates` — browse every saved bundle in one
 * table, edit names/descriptions/feature sets, and delete templates that
 * are no longer useful.
 *
 * PMs can only edit/delete templates they themselves created; admins can
 * touch anything (backend enforced on PATCH + DELETE).
 */
export default function PermissionTemplatesPage() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);           // [{key, label, category, base_roles}]
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);         // null | 'new' | template object
  const [form, setForm] = useState({ name: "", description: "", features: new Set() });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bulkTemplate, setBulkTemplate] = useState(null);   // template being bulk-applied
  const [bulkProperty, setBulkProperty] = useState("");
  const [bulkUsers, setBulkUsers] = useState(new Set());
  const [bulkUserList, setBulkUserList] = useState([]);
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkResult, setBulkResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, tpl] = await Promise.all([
        api.get("/admin/permissions/catalog"),
        api.get("/admin/permissions/templates"),
      ]);
      setCatalog(cat.data.features || []);
      setTemplates(tpl.data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load templates");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const out = {};
    for (const f of catalog) (out[f.category] ||= []).push(f);
    return out;
  }, [catalog]);

  const openCreate = () => {
    setForm({ name: "", description: "", features: new Set() });
    setEditing("new");
  };
  const openEdit = (t) => {
    setForm({ name: t.name, description: t.description || "", features: new Set(t.features || []) });
    setEditing(t);
  };

  const toggle = (key) => {
    setForm((f) => {
      const n = new Set(f.features);
      if (n.has(key)) n.delete(key); else n.add(key);
      return { ...f, features: n };
    });
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Give the template a name");
    if (form.features.size === 0) return toast.error("Pick at least one feature");
    const payload = { name, description: form.description.trim() || null, features: [...form.features] };
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/admin/permissions/templates", payload);
        toast.success(`Template "${name}" created`);
      } else {
        await api.patch(`/admin/permissions/templates/${editing.id}`, payload);
        toast.success(`Template "${name}" updated`);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/permissions/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  const canEditTemplate = (t) =>
    user?.role === "admin" || t.created_by === user?.id;

  const openBulkApply = async (t) => {
    setBulkTemplate(t);
    setBulkResult(null);
    setBulkUsers(new Set());
    setBulkSearch("");
    setBulkProperty((user?.property_ids || [])[0] || "");
    try {
      // Server-side scope-friendly listing; UI further filters + hides admins/PMs.
      const { data } = await api.get("/admin/users?limit=500");
      const items = (data.items || []).filter(
        (u) => !["admin", "property_manager"].includes(u.role) && (u.property_ids || []).length > 0,
      );
      setBulkUserList(items);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load users");
    }
  };

  const eligibleUsers = useMemo(() => {
    if (!bulkTemplate || !bulkProperty) return [];
    const q = bulkSearch.trim().toLowerCase();
    return bulkUserList
      .filter((u) => (u.property_ids || []).includes(bulkProperty))
      .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [bulkTemplate, bulkProperty, bulkSearch, bulkUserList]);

  const toggleBulkUser = (uid) => {
    setBulkUsers((prev) => {
      const n = new Set(prev);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      return n;
    });
  };
  const toggleBulkAll = () => {
    setBulkUsers((prev) => {
      if (prev.size === eligibleUsers.length) return new Set();
      return new Set(eligibleUsers.map((u) => u.id));
    });
  };

  const runBulkApply = async (dryRun = false) => {
    if (!bulkTemplate || !bulkProperty || bulkUsers.size === 0) return;
    setBusy(true);
    if (dryRun) setBulkResult(null);
    try {
      const { data } = await api.post(`/admin/permissions/templates/${bulkTemplate.id}/bulk-apply`, {
        template_id: bulkTemplate.id,
        property_id: bulkProperty,
        user_ids: [...bulkUsers],
        merge: true,
        dry_run: dryRun,
      });
      setBulkResult(data);
      if (dryRun) {
        toast.info(`Preview ready — ${data.applied} eligible · ${data.skipped} would be skipped`);
      } else {
        toast.success(`Template applied to ${data.applied} user${data.applied === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Bulk apply failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="permission-templates-page">
      <PageHeader
        title="Permission templates"
        subtitle="Save common permission bundles and apply them to new users in one click."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} className="border-white/10 gap-1 h-9" data-testid="templates-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
            <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 gap-2 h-9" data-testid="templates-new-btn">
              <Plus className="h-4 w-4" /> New template
            </Button>
          </div>
        }
      />

      <Card className="border-white/10 bg-white/[0.02]">
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">Loading templates…</div>
        ) : templates.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500">
            No templates yet. Click <span className="text-orange-300">New template</span> to save your first bundle.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {templates.map((t) => (
              <div key={t.id} className="p-4 flex items-start gap-4" data-testid={`template-row-${t.id}`}>
                <Bookmark className="h-4 w-4 text-emerald-400 mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold">{t.name}</div>
                    <Badge variant="outline" className="border-emerald-500/30 text-emerald-200 text-[10px]">
                      {t.features.length} feature{t.features.length === 1 ? "" : "s"}
                    </Badge>
                    <span className="text-[10px] text-zinc-500">
                      by {t.created_by_name} · {new Date(t.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {t.description && (
                    <div className="text-xs text-zinc-400 mt-1">{t.description}</div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`template-features-${t.id}`}>
                    {t.features.slice(0, 12).map((k) => (
                      <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/5 font-mono text-zinc-300">
                        {k}
                      </span>
                    ))}
                    {t.features.length > 12 && (
                      <span className="text-[10px] text-zinc-500 px-1.5 py-0.5">+{t.features.length - 12} more</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openBulkApply(t)}
                          className="h-7 text-xs gap-1 text-emerald-300"
                          data-testid={`template-bulk-apply-${t.id}`}>
                    <Users2 className="h-3 w-3" /> Apply to users
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(t)}
                          disabled={!canEditTemplate(t)}
                          title={canEditTemplate(t) ? "Edit template" : "Only the creator or an admin can edit this"}
                          className="h-7 text-xs gap-1 text-orange-300"
                          data-testid={`template-edit-${t.id}`}>
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t)}
                          disabled={!canEditTemplate(t)}
                          className="h-7 text-xs gap-1 text-rose-300"
                          data-testid={`template-delete-${t.id}`}>
                    <Trash2 className="h-3 w-3" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto"
                          data-testid="template-editor-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
                {editing === "new" ? "New template" : `Edit "${editing.name}"`}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Pick the features this bundle grants. Applying it to a user later adds these features on top of their base role.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Name</label>
                <Input value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       placeholder='e.g. "Front Desk Package"'
                       className="bg-white/[0.03] border-white/10 mt-1"
                       data-testid="template-form-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Description (optional)</label>
                <Textarea value={form.description}
                          onChange={(e) => setForm({ ...form, description: e.target.value })}
                          placeholder="What this bundle unlocks and when to use it"
                          rows={2}
                          className="bg-white/[0.03] border-white/10 mt-1 text-sm"
                          data-testid="template-form-description" />
              </div>

              <div className="space-y-3 mt-2">
                {Object.entries(grouped).map(([category, features]) => (
                  <div key={category} className="bg-white/[0.02] border border-white/10 rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{category}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-1.5 gap-x-4">
                      {features.map((f) => (
                        <label key={f.key} className="flex items-start gap-2 cursor-pointer">
                          <Checkbox checked={form.features.has(f.key)}
                                    onCheckedChange={() => toggle(f.key)}
                                    className="mt-0.5"
                                    data-testid={`template-feature-${f.key}`} />
                          <div className="text-sm leading-tight">
                            <div>{f.label}</div>
                            <div className="text-[10px] text-zinc-500 font-mono">{f.key}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={() => setEditing(null)} className="border-white/10">Cancel</Button>
              <Button onClick={save} disabled={busy}
                      className="bg-orange-500 hover:bg-orange-600"
                      data-testid="template-save">
                {busy ? "Saving…" : (editing === "new" ? "Create template" : "Save changes")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {bulkTemplate && (
        <Dialog open onOpenChange={(o) => { if (!o) { setBulkTemplate(null); setBulkResult(null); } }}>
          <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto"
                          data-testid="bulk-apply-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users2 className="h-5 w-5 text-emerald-400" />
                Apply &ldquo;{bulkTemplate.name}&rdquo; to multiple users
              </DialogTitle>
              <DialogDescription className="text-xs">
                Pick a community and check every user who should receive this bundle. Admins + PMs are excluded (they already have everything).
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">Community</span>
                <Select value={bulkProperty} onValueChange={setBulkProperty}>
                  <SelectTrigger className="bg-white/[0.02] border-white/10 h-9 w-64"
                                  data-testid="bulk-apply-property-picker">
                    <SelectValue placeholder="Select community" />
                  </SelectTrigger>
                  <SelectContent>
                    {(user?.property_ids || []).map((pid) => (
                      <SelectItem key={pid} value={pid}>{pid}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="ml-auto flex items-center gap-2">
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-200">
                    {bulkTemplate.features.length} feature{bulkTemplate.features.length === 1 ? "" : "s"}
                  </Badge>
                  <Badge variant="outline" className="border-orange-500/40 text-orange-300">
                    {bulkUsers.size} selected
                  </Badge>
                </div>
              </div>

              <Input value={bulkSearch}
                      onChange={(e) => setBulkSearch(e.target.value)}
                      placeholder="Search users by name or email…"
                      className="bg-white/[0.03] border-white/10"
                      data-testid="bulk-apply-search" />

              {bulkResult ? (
                <div className="bg-white/[0.02] border border-white/10 rounded-md p-3 max-h-60 overflow-y-auto space-y-1"
                      data-testid="bulk-apply-results">
                  <div className="text-xs text-zinc-400 mb-2 flex items-center gap-2">
                    <span className="text-emerald-300 font-semibold">{bulkResult.applied}</span> {bulkResult.dry_run ? "would be applied" : "applied"} ·{" "}
                    <span className="text-rose-300">{bulkResult.skipped}</span> skipped
                    {bulkResult.dry_run && (
                      <Badge variant="outline" className="border-indigo-500/40 text-indigo-300 text-[10px] ml-auto" data-testid="bulk-apply-dryrun-badge">
                        preview · nothing saved
                      </Badge>
                    )}
                  </div>
                  {bulkResult.results.map((r) => (
                    <div key={r.user_id} className="text-xs flex items-start gap-2" data-testid={`bulk-apply-result-${r.user_id}`}>
                      {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5" /> : <AlertCircle className="h-3.5 w-3.5 text-rose-400 mt-0.5" />}
                      <div className="flex-1 min-w-0">
                        <div className={r.ok ? "text-emerald-200" : "text-rose-300"}>
                          {r.name || r.user_id} <span className="text-zinc-500 text-[10px]">({r.role || "?"})</span>
                          {!r.ok && <span className="text-[10px] text-zinc-500 ml-1">— {r.reason}</span>}
                        </div>
                        {r.ok && bulkResult.dry_run && (r.will_add?.length > 0 || r.already_has?.length > 0) && (
                          <div className="text-[10px] text-zinc-500 mt-0.5">
                            {r.will_add?.length > 0 && (
                              <span className="text-emerald-400">+{r.will_add.length} new · </span>
                            )}
                            {r.already_has?.length > 0 && (
                              <span className="text-zinc-500">{r.already_has.length} already effective</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-xs">
                    <Checkbox checked={eligibleUsers.length > 0 && bulkUsers.size === eligibleUsers.length}
                              onCheckedChange={toggleBulkAll}
                              data-testid="bulk-apply-select-all" />
                    <span className="text-zinc-400">
                      Select all {eligibleUsers.length} eligible user{eligibleUsers.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="border border-white/10 rounded-md max-h-72 overflow-y-auto divide-y divide-white/5"
                        data-testid="bulk-apply-user-list">
                    {eligibleUsers.length === 0 ? (
                      <div className="p-6 text-center text-xs text-zinc-500 italic">
                        {bulkProperty ? "No eligible users on this community" : "Pick a community above"}
                      </div>
                    ) : (
                      eligibleUsers.map((u) => (
                        <label key={u.id} className="flex items-center gap-3 p-2 hover:bg-white/[0.02] cursor-pointer">
                          <Checkbox checked={bulkUsers.has(u.id)}
                                    onCheckedChange={() => toggleBulkUser(u.id)}
                                    data-testid={`bulk-apply-user-${u.id}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{u.name || u.email}</div>
                            <div className="text-[10px] text-zinc-500 truncate">{u.email} · {u.role}{u.unit ? ` · ${u.unit}` : ""}</div>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={() => { setBulkTemplate(null); setBulkResult(null); }} className="border-white/10">
                {bulkResult && !bulkResult.dry_run ? "Close" : "Cancel"}
              </Button>
              {(!bulkResult || bulkResult.dry_run) && (
                <>
                  <Button variant="outline" onClick={() => runBulkApply(true)}
                          disabled={busy || !bulkProperty || bulkUsers.size === 0}
                          className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10"
                          data-testid="bulk-apply-preview">
                    {busy ? "Loading…" : "Preview"}
                  </Button>
                  <Button onClick={() => runBulkApply(false)}
                          disabled={busy || !bulkProperty || bulkUsers.size === 0}
                          className="bg-emerald-500 hover:bg-emerald-600"
                          data-testid="bulk-apply-run">
                    {busy ? "Applying…" : `Apply to ${bulkUsers.size} user${bulkUsers.size === 1 ? "" : "s"}`}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
